import { useState } from "react";
import { Eye } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, CardContent, CardHeader, CardTitle, Label, Textarea } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { DialogoDeAcao } from "./DialogoDeAcao";

const dataHora = (d: Date | string) => new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** "Ver como": só com a liberação da pessoa, com motivo, 30 min, só leitura — tudo na Auditoria. */
export function VerComo({ userId }: { userId: string }) {
  const utils = trpc.useUtils();
  const { data } = trpc.admin.verComo.estado.useQuery({ userId });
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const atualizar = () => {
    utils.admin.verComo.estado.invalidate({ userId });
    utils.admin.auditoria.list.invalidate();
  };
  const iniciar = trpc.admin.verComo.iniciar.useMutation({ onError: (e) => toast.error(e.message) });
  const encerrar = trpc.admin.verComo.encerrar.useMutation({ onSuccess: atualizar, onError: (e) => toast.error(e.message) });

  const confirmar = () => {
    // Janela aberta no clique: aberta depois da resposta, o navegador trata como pop-up e bloqueia.
    const aba = window.open("about:blank", "_blank");
    iniciar.mutate(
      { userId, motivo },
      {
        onSuccess: (r) => {
          if (aba) aba.location.href = r.url;
          else window.open(r.url, "_blank");
          setAberto(false);
          setMotivo("");
          atualizar();
        },
        onError: () => aba?.close(),
      },
    );
  };

  const liberadoAte = data?.liberadoAte;
  const sessoes = data?.sessoes ?? [];
  return (
    <Card className="border-admin-200">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Ver como</CardTitle>
          <p className="mt-1 text-xs text-admin-700">
            {liberadoAte ? `Liberado pela pessoa até ${dataHora(liberadoAte)}.` : "A pessoa não liberou o acesso (Minha conta › Privacidade e dados)."}
          </p>
        </div>
        <Button size="sm" disabled={!liberadoAte} onClick={() => setAberto(true)}>
          <Eye className="mr-1.5 h-3.5 w-3.5" />
          Ver como
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        {sessoes.length === 0 ? (
          <p className="px-6 pb-5 text-sm text-admin-700">Nenhum acesso.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {sessoes.map((s) => {
                const ativa = !s.encerradaEm && new Date(s.expiraEm) > new Date();
                return (
                  <tr key={s.id} className="border-t border-zinc-100 align-top">
                    <td className="whitespace-nowrap px-4 py-2.5 text-admin-700">{dataHora(s.criadaEm)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-admin-900">{s.motivo}</span>
                      <span className="block text-xs text-admin-700">
                        {s.admin.name ?? s.admin.email} · <span title={s.caminhos.join(", ")}>{s.caminhos.length} tipo{s.caminhos.length === 1 ? "" : "s"} de dado lido{s.caminhos.length === 1 ? "" : "s"}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {ativa ? (
                        <Button size="sm" variant="outline" disabled={encerrar.isPending} onClick={() => encerrar.mutate({ sessaoId: s.id })}>Encerrar</Button>
                      ) : (
                        <span className="text-xs text-admin-700">Encerrada</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
      <DialogoDeAcao
        aberto={aberto}
        onFechar={() => setAberto(false)}
        titulo="Ver como"
        rotulo="Abrir em nova aba"
        descricao="Abre a conta da pessoa numa aba nova, só leitura, por 30 minutos. Ela recebe uma notificação e o acesso fica na Auditoria."
        pronto={motivo.trim().length >= 10}
        ocupado={iniciar.isPending}
        onConfirmar={confirmar}
      >
        <Label htmlFor="motivo-ver-como">Motivo (a pessoa vê)</Label>
        <Textarea id="motivo-ver-como" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </DialogoDeAcao>
    </Card>
  );
}
